import React, { FC, useState, useCallback, useRef, useLayoutEffect } from "react";
import { observer } from "mobx-react-lite";
import { Droppable } from "@hello-pangea/dnd";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import { Pane, Tab } from "@/store/navigation/types";
import { ObservableMap } from "mobx";
import { ScrollDirection, useHorizontalScroll } from "@/mds/components/layout/horizontal-scroll";
import { ZIndex } from "@/domains/design/constants";
import { DraggableTab } from "./draggable-tab";
import { CloseAllTabsModal } from "./close-all-tabs-modal";
import { useTabDragContext } from "./tab-bar-context-provider";
import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { MDSHomeTab } from "@/mds/components/tabs/home-tab";

interface TabBarProps {
  pane: Pane;
  hiddenPanes?: Pane[];
  tabsMap?: ObservableMap<string, Tab>;
  canShowSplitButton?: boolean;
  savedScrollPosition?: number;
  onSavedScrollPositionChange?: (left: number) => void;
}

export const TabBar: FC<TabBarProps> = observer(
  ({
    pane,
    hiddenPanes = [],
    canShowSplitButton = true,
    savedScrollPosition = 0,
    onSavedScrollPositionChange,
  }) => {
    const { store } = useAppStore();
    const { isAnyTabDragging } = useTabDragContext();
    const numberOfPanes = store.navigation.tabs.panes.length;
    const paneTabs = store.navigation.tabs.getTabsForPane(pane.id);
    const activeTabId = pane.activeTabId;
    const isActivePane = pane.id === store.navigation.tabs.activePaneId;

    const isSingleVisiblePaneMode = hiddenPanes.length > 0;
    const { scrollContainerRef, scrollState, handleScroll, startScrolling, stopScrolling } =
      useHorizontalScroll();

    const [isCloseAllOpen, setIsCloseAllOpen] = useState(false);
    const closeAllTabsModalRef = useRef<HTMLButtonElement>(null);

    const handleTabClick = (tabId: string) => {
      store.navigation.tabs.switchToTab({ tabId });
    };

    const handleCloseTab = (tabId: string) => {
      store.navigation.tabs.closeTab({ tabId });
    };

    const handleSplitPanel = (e: React.MouseEvent) => {
      store.navigation.tabs.createPane({ afterPaneId: pane.id });
      e.stopPropagation(); // Prevents the click from focusing the panel again
    };

    const handleSplitPaneWithTab = (tabId: string) => {
      store.navigation.tabs.splitPaneWithTab({ tabId });
    };

    const handleOpenCloseAllModal = (e: React.MouseEvent) => {
      if (paneTabs.length === 1) handleConfirmCloseAll();
      else setIsCloseAllOpen(true);
      e.stopPropagation(); // Prevents the click from focusing the panel again
    };

    const handleCancelCloseAll = useCallback(() => setIsCloseAllOpen(false), []);

    const handleConfirmCloseAll = useCallback(() => {
      store.navigation.tabs.closePane({ paneId: pane.id });
      setIsCloseAllOpen(false);
    }, [pane.id, store.navigation.tabs]);

    const defaultTab = paneTabs.find(tab => tab.isDefault);
    const visibleTabs = hiddenPanes?.length
      ? store.navigation.tabs.allTabsFlattened
      : paneTabs.filter(tab => !tab.isDefault);

    // Restore saved scroll position when entering single-pane-visible mode
    useLayoutEffect(() => {
      if (!isSingleVisiblePaneMode) return;
      const container = scrollContainerRef.current;
      if (!container) return;
      container.scrollLeft = savedScrollPosition || 0;
      handleScroll();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSingleVisiblePaneMode, savedScrollPosition]);

    const handleScrollWithMemory = useCallback(() => {
      handleScroll();
      if (!isSingleVisiblePaneMode) return;
      const container = scrollContainerRef.current;
      if (container) onSavedScrollPositionChange?.(container.scrollLeft);
    }, [handleScroll, isSingleVisiblePaneMode, onSavedScrollPositionChange, scrollContainerRef]);

    // Auto-scroll to active tab when it changes
    useLayoutEffect(() => {
      if (!isActivePane || !activeTabId || !scrollContainerRef.current) return;

      const timeoutId = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const activeTab = container.querySelector(`[data-tab-id="${activeTabId}"]`);
        if (!activeTab) return;

        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        const isFullyVisible =
          tabRect.left >= containerRect.left && tabRect.right <= containerRect.right;

        if (!isFullyVisible) {
          const scrollAmount =
            tabRect.left < containerRect.left
              ? tabRect.left - containerRect.left
              : tabRect.right - containerRect.right;
          container.scrollTo({ left: container.scrollLeft + scrollAmount, behavior: "smooth" });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }, [activeTabId, isActivePane, scrollContainerRef]);

    if (paneTabs.length === 0) return null;
    return (
      <StyledTabBarContainer>
        {defaultTab && (
          <MDSHomeTab
            isActive={defaultTab.id === activeTabId && isActivePane}
            isOnlyTab={paneTabs.length === 1}
            onClick={() => handleTabClick(defaultTab.id)}
            data-tab-id={defaultTab.id}
          />
        )}

        <StyledTabBarMain>
          {scrollState.leftVisible && (
            <LeftTabScrollButton
              variant={MDSIconButtonVariant.TabBar}
              iconKind={MDSIconKind.AngleLeft}
              $visible={scrollState.leftVisible}
              onPressStart={() => startScrolling(ScrollDirection.Left)}
              onPressEnd={() => stopScrolling()}
            />
          )}
          <Droppable droppableId={pane.id} direction="horizontal">
            {provided => (
              <StyledScrollableTabsContainer
                ref={node => {
                  provided.innerRef(node);
                  scrollContainerRef.current = node;
                }}
                {...provided.droppableProps}
                onScroll={handleScrollWithMemory}
                $isDropActive={isAnyTabDragging}
              >
                {visibleTabs.map((tab, index) => (
                  <DraggableTab
                    key={tab.id}
                    tab={tab}
                    index={index}
                    isActive={tab.id === activeTabId && isActivePane}
                    canShowSplitPaneButton={canShowSplitButton}
                    onTabClick={handleTabClick}
                    onCloseTab={handleCloseTab}
                    onSplitPaneWithTab={handleSplitPaneWithTab}
                  />
                ))}
                {provided.placeholder}
              </StyledScrollableTabsContainer>
            )}
          </Droppable>
          {scrollState.rightVisible && (
            <RightTabScrollButton
              variant={MDSIconButtonVariant.TabBar}
              iconKind={MDSIconKind.AngleRight}
              $visible={scrollState.rightVisible}
              onPressStart={() => startScrolling(ScrollDirection.Right)}
              onPressEnd={() => stopScrolling()}
            />
          )}
        </StyledTabBarMain>

        <StyledTabBarActions>
          {canShowSplitButton && (
            <MDSTooltip
              config={{
                label: "Split Panel",
                placement: MDSTooltipPlacement.Bottom,
              }}
            >
              <StyledTabBarActionIconButton
                size={MDSIconButtonSize.Medium}
                iconKind={MDSIconKind.SplitPanel}
                onClick={handleSplitPanel}
              />
            </MDSTooltip>
          )}
          {(paneTabs.length > 1 || numberOfPanes > 1) && (
            <MDSTooltip
              config={{
                label: "Close All Tabs",
                placement: MDSTooltipPlacement.Bottom,
              }}
            >
              <StyledTabBarActionIconButton
                ref={closeAllTabsModalRef}
                size={MDSIconButtonSize.Medium}
                iconKind={MDSIconKind.Exit}
                onClick={handleOpenCloseAllModal}
              />
            </MDSTooltip>
          )}
          <CloseAllTabsModal
            triggerRef={closeAllTabsModalRef}
            isOpen={isCloseAllOpen}
            count={numberOfPanes > 1 ? paneTabs.length : paneTabs.length - 1}
            onCancel={handleCancelCloseAll}
            onConfirm={handleConfirmCloseAll}
          />
        </StyledTabBarActions>
      </StyledTabBarContainer>
    );
  }
);

const StyledTabBarContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.chrome};
`;

const StyledScrollableTabsContainer = styled.div<{ $isDropActive: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-shrink: 1;
  width: 100%;
  height: 40px;
  background-color: ${({ theme, $isDropActive }) =>
    $isDropActive ? theme.colors.background.diff : theme.colors.background.button.tabBar.default};

  overflow-x: scroll;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledTabBarMain = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  height: 40px;
  flex-shrink: 1;
  min-width: 0;
  background-color: ${({ theme }) => theme.colors.background.button.tabBar.default};
`;

const StyledTabBarActions = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.background.button.tabBar.default};
`;

const StyledTabBarActionIconButton = styled(MDSIconButton)`
  background-color: ${({ theme }) => theme.colors.background.button.tabBar.default};
  border-radius: ${({ theme }) => theme.borderRadius.none};
  border-left: ${({ theme }) => `1px solid ${theme.colors.border.chrome}`};

  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.button.tabBar.hover};
  }
`;

export const LeftTabScrollButton = styled(MDSIconButton)<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  z-index: ${ZIndex.Shadow};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  box-shadow: ${({ theme }) => theme.shadows.tabScrollButtonLeft};
  border-radius: 0px;
  border-top-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
`;

export const RightTabScrollButton = styled(MDSIconButton)<{ $visible: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  z-index: ${ZIndex.Shadow};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  box-shadow: ${({ theme }) => theme.shadows.tabScrollButtonRight};
  border-radius: 0px;
  border-top-left-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.xlarge};
`;
